import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Search, ChevronLeft, Loader2, AppWindow, FileText, Crown } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import { Alert, AlertDescription } from "@shadcn-in-nx/ui/alert";

export function AppLinkSearchModal({ open, onOpenChange, onSelect, currentWorkflowId }) {
    const { t } = useTranslation();
    const { isFeatureEnabled } = useAuth();
    const hasDeepLinkingAccess = isFeatureEnabled("deepLinking");
    const [searchTerm, setSearchTerm] = useState("");
    const [apps, setApps] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [loading, setLoading] = useState(false);

    const { call: fetchWorkflows } = useBluelagoon({ method: "getCompanyWorkflowsWithPages" });
    const { call: updateWorkflow } = useBluelagoon({ method: "updateWorkflow" });

    useEffect(() => {
        if (!open) { setSelectedApp(null); setSearchTerm(""); return; }
        if (!hasDeepLinkingAccess) return;
        setLoading(true);
        fetchWorkflows()
            .then((res) => { if (res?.data?.workflows) setApps(res.data.workflows); })
            .finally(() => setLoading(false));
    }, [open, fetchWorkflows, hasDeepLinkingAccess]);

    const handlePageSelect = async (app, page) => {
        const domain = app.customDomain || app.internalDomain;
        onSelect({ appName: app.name, pageName: page.name, domain });
        onOpenChange(false);

        try {
            await Promise.all([
                updateWorkflow({ id: currentWorkflowId, isDeepLinkEnabled: true }),
                updateWorkflow({ id: app.id, isDeepLinkEnabled: true }),
            ]);
        } catch {
            // Silent fail — deep linking still works via redirect fallback
        }
    };

    const filtered = apps
        .filter((app) => app.id !== currentWorkflowId)
        .filter((app) => !searchTerm || app.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const pages = selectedApp?.pages?.filter((p) =>
        !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 min-w-0">
                        {selectedApp && hasDeepLinkingAccess && (
                            <button onClick={() => { setSelectedApp(null); setSearchTerm(""); }} className="hover:bg-gray-100 rounded p-1 flex-shrink-0">
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                        )}
                        <span className="truncate">{selectedApp ? selectedApp.name : t("appPreview.linkedApps", "Linked Apps")}</span>
                    </DialogTitle>
                </DialogHeader>

                {!hasDeepLinkingAccess ? (
                    <div className="py-6 space-y-4">
                        <Alert className="bg-purple-50 border-purple-200">
                            <Crown className="h-4 w-4 text-purple-600" />
                            <AlertDescription className="text-purple-700">
                                {t("appPreview.deepLinkingUpgradeMessage") ||
                                    "Deep linking is available on Innovator plan and above. Upgrade your plan to enable cross-app navigation."}
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder={selectedApp ? t("appPreview.searchPages", "Search pages...") : t("appPreview.searchApps", "Search apps...")}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                autoFocus
                            />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto space-y-1">
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                </div>
                            ) : !selectedApp ? (
                                filtered.length === 0 ? (
                                    <p className="text-center py-8 text-gray-500 text-sm">{t("appPreview.noAppsFound", "No apps found")}</p>
                                ) : filtered.map((app) => (
                                    <button key={app.id} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left" onClick={() => { setSelectedApp(app); setSearchTerm(""); }}>
                                        <div className="p-2 bg-blue-50 rounded-md flex-shrink-0"><AppWindow className="h-4 w-4 text-blue-500" /></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{app.name}</p>
                                            <p className="text-xs text-gray-500">{app.pages?.length || 0} pages</p>
                                        </div>
                                    </button>
                                ))
                            ) : pages.length === 0 ? (
                                <p className="text-center py-8 text-gray-500 text-sm">{t("appPreview.noPagesFound", "No pages found")}</p>
                            ) : pages.map((page) => (
                                <button key={page.id} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left" onClick={() => handlePageSelect(selectedApp, page)}>
                                    <div className="p-2 bg-green-50 rounded-md flex-shrink-0"><FileText className="h-4 w-4 text-green-500" /></div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{page.name}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
